import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { epcService } from '@/services/epc.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ENTITY_TYPE_EPC_PERMIT } from '@/constants/approval-entity-types';

const TYPES = [
  'Hot_Work',
  'Confined_Space',
  'Excavation',
  'Lifting',
  'Electrical',
  'Working_at_Height',
  'Isolation',
  'Other',
];
/** Operational statuses only — Approved/Rejected come from Approval Runtime. */
const OPERATIONAL_STATUSES = ['Draft', 'Submitted', 'Active', 'Suspended', 'Expired', 'Closed'];

type Props = { projectId?: string };

export const PermitsPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const watchedPackageId = Form.useWatch('packageId', form);

  useEffect(() => {
    const pid = searchParams.get('permitId');
    if (pid) setSelectedId(pid);
  }, [searchParams]);

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const msDocsQ = useQuery({
    queryKey: ['cep-ms-docs', projectId, watchedPackageId],
    queryFn: () =>
      epcService.getCepFieldDocuments(projectId, {
        packageId: watchedPackageId,
        docType: 'Method_Statement',
      }),
    enabled: !!projectId && open,
    staleTime: 15_000,
  });

  const permitsQ = useQuery({
    queryKey: ['cep-permits', projectId],
    queryFn: () => epcService.getCepPermits(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createPermit({ ...values, projectId, status: 'Draft' }),
    onSuccess: () => {
      message.success('Permit registered (Draft)');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-permits'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      epcService.patchPermit(id, { status }),
    onSuccess: () => {
      message.success('Permit updated');
      void qc.invalidateQueries({ queryKey: ['cep-permits'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const submitMut = useMutation({
    mutationFn: (id: string) => epcService.submitPermitForApproval(id),
    onSuccess: () => {
      message.success('Submitted to Approval Runtime');
      void qc.invalidateQueries({ queryKey: ['cep-permits'] });
      void qc.invalidateQueries({ queryKey: ['approval'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for permits</Tag>;

  const rows = (permitsQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];
  const msDocs = ((msDocsQ.data as any[]) || []).filter(
    (d) => Number(d.is_approved_revision) === 1 || d.is_approved_revision === true
  );
  const selected = rows.find((r) => String(r.id) === selectedId) || null;

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Register permit
        </Button>
        <Tag color="geekblue">PTW → Approval Runtime</Tag>
        <Tag>Approved/Rejected only via inbox workflows (`epc_permit`)</Tag>
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Configure a Platform approval workflow with entity type epc_permit"
        description="Use scripts/setup-epc-permit-workflow.ts (or Platform → Approval Workflows). If a method statement is linked, it must be an approved Field Document before submit."
      />
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={permitsQ.isLoading}
        dataSource={rows}
        onRow={(r) => ({
          onClick: () => {
            setSelectedId(String(r.id));
            const next = new URLSearchParams(searchParams);
            next.set('tab', 'permits');
            next.set('permitId', String(r.id));
            setSearchParams(next, { replace: true });
          },
        })}
        columns={[
          { title: 'Type', dataIndex: 'permit_type', width: 140 },
          { title: 'Number', dataIndex: 'permit_number', width: 120 },
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (v: string, r: any) => {
              const locked = v === 'Approved' || v === 'Rejected' || v === 'Submitted';
              if (locked) return <Tag color={v === 'Approved' ? 'green' : v === 'Rejected' ? 'red' : 'blue'}>{v}</Tag>;
              return (
                <Select
                  size="small"
                  style={{ width: 130 }}
                  value={v}
                  options={OPERATIONAL_STATUSES.filter((s) => s !== 'Submitted').map((s) => ({
                    value: s,
                    label: s,
                  }))}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(status) => patchMut.mutate({ id: String(r.id), status })}
                />
              );
            },
          },
          {
            title: 'MS',
            dataIndex: 'method_statement_approved',
            width: 70,
            render: (v: number) => (v ? <Tag color="green">Yes</Tag> : <Tag>—</Tag>),
          },
          {
            title: 'Actions',
            width: 160,
            render: (_: unknown, r: any) =>
              ['Draft', 'Rejected'].includes(String(r.status)) ||
              (String(r.status) === 'Submitted' && !r.approval_instance_id) ? (
                <Button
                  size="small"
                  type="link"
                  loading={submitMut.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    submitMut.mutate(String(r.id));
                  }}
                >
                  Submit for approval
                </Button>
              ) : null,
          },
        ]}
      />

      <Drawer
        title={selected ? `Permit — ${selected.title}` : 'Permit'}
        open={!!selected}
        width={520}
        onClose={() => {
          setSelectedId(null);
          const next = new URLSearchParams(searchParams);
          next.delete('permitId');
          setSearchParams(next, { replace: true });
        }}
      >
        {selected && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Tag>{selected.permit_type}</Tag>
            <Tag color="blue">{selected.status}</Tag>
            {selected.department_id ? (
              <div>
                <span style={{ color: 'rgba(0,0,0,0.45)' }}>Department ID: </span>
                {String(selected.department_id)}
              </div>
            ) : null}
            {['Draft', 'Rejected'].includes(String(selected.status)) && (
              <Button
                type="primary"
                block
                loading={submitMut.isPending}
                onClick={() => submitMut.mutate(String(selected.id))}
              >
                Submit for approval
              </Button>
            )}
            {selected.status === 'Approved' && (
              <Button
                block
                onClick={() => patchMut.mutate({ id: String(selected.id), status: 'Active' })}
              >
                Mark Active
              </Button>
            )}
            <ApprovalPanel entityType={ENTITY_TYPE_EPC_PERMIT} entityId={String(selected.id)} />
          </Space>
        )}
      </Drawer>

      <Modal
        title="Register permit"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ methodStatementApproved: false }}
        >
          <Form.Item name="permitType" label="Type" rules={[{ required: true }]}>
            <Select options={TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
            extra="Inherits from the project when set; confirm or override."
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="permitNumber" label="Permit number">
            <Input />
          </Form.Item>
          <Form.Item name="packageId" label="Execution package">
            <Select
              allowClear
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="methodStatementDocumentId"
            label="Method statement (approved field document)"
            extra={
              msDocs.length
                ? undefined
                : 'Create an approved Method_Statement under Field Documents for this package first (or clear selection).'
            }
          >
            <Select
              allowClear
              placeholder="Select approved MS"
              options={msDocs.map((d: any) => ({
                value: String(d.id),
                label: `${d.document_ref || d.title}${d.revision ? ` (${d.revision})` : ''}`,
              }))}
              onChange={(id) => {
                const doc = msDocs.find((d: any) => String(d.id) === String(id));
                if (doc) {
                  form.setFieldsValue({
                    methodStatementDocumentId: String(doc.id),
                    methodStatementRef: doc.document_ref || doc.title,
                    methodStatementRevision: doc.revision || undefined,
                    methodStatementApproved: true,
                  });
                } else {
                  form.setFieldsValue({
                    methodStatementDocumentId: undefined,
                    methodStatementRef: undefined,
                    methodStatementRevision: undefined,
                    methodStatementApproved: false,
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item name="methodStatementRef" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="methodStatementRevision" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="methodStatementApproved" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="issuer" label="Issuer">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermitsPanel;
